import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Book, Trophy, Star, Crown, Medal, ArrowLeft } from '@phosphor-icons/react';
import GlobalHeader from '../components/GlobalHeader';

const badgeIcons = {
  novice_writer: { icon: <Star size={16} weight="duotone" />, color: 'text-blue-500', label: 'Novice' },
  rising_writer: { icon: <Trophy size={16} weight="duotone" />, color: 'text-amber-500', label: 'Rising' },
  verified_writer: { icon: <Medal size={16} weight="duotone" />, color: 'text-purple-500', label: 'Verified' },
  top_writer: { icon: <Crown size={16} weight="duotone" />, color: 'text-primary', label: 'Top Writer' }
};

export const Leaderboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leaderboard`)
      .then(res => setUsers(res.data.users))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <Crown size={40} weight="duotone" className="text-primary" />
            <h2 className="text-5xl md:text-6xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Leaderboard
            </h2>
          </div>
          <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
            Top writers ranked by community engagement. Earn points by publishing articles, receiving likes and comments.
          </p>

          {users.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center">
              <Trophy size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No rankings yet. Be the first to earn points!</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {users.map((user, idx) => (
                <Link
                  key={user.user_id}
                  to={`/profile/${user.user_id}`}
                  data-testid={`leaderboard-user-${user.user_id}`}
                  className={`flex items-center gap-4 p-4 border-b border-border last:border-b-0 hover:bg-muted transition-colors ${idx < 3 ? 'bg-primary/5' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                    idx === 0 ? 'bg-yellow-500 text-white' :
                    idx === 1 ? 'bg-gray-400 text-white' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </div>
                  <img
                    src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`}
                    alt={user.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{user.name}</p>
                      {user.badges?.map((b, i) => badgeIcons[b] && (
                        <span key={i} className={badgeIcons[b].color} title={badgeIcons[b].label}>
                          {badgeIcons[b].icon}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">{user.followers_count} followers</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{user.points}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">points</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
